/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./SwingieResult.css";
import { useAppContext } from "../../../../AppContext";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import Modal from "./Modal";
import {
  BODY_STORE_NAME,
  FACE_STORE_NAME,
  deleteSelectedImages,
} from "../../../../IndexedDBUtils";
// Import GIFs locally instead of fetching from the server
import gifSavingResult from "../../../../assets/gifs/result-saving_result.gif";
import gifDone from "../../../../assets/gifs/result-done.gif";
import gifFailed from "../../../../assets/gifs/result-failed.gif";
import gifFaceFailed from "../../../../assets/gifs/result-face_failed.gif";
import { SERVER_URL } from "../../../../config/serverUrl";
import { getEventScope, isCurrentScope } from "../../../../eventIdentity";
import {
  clearClaimState,
  ensureApproveRequest,
  readClaimState,
  writeClaimState,
} from "../../shared/faceClaimClient";
import {
  MSG_FAILED,
  MSG_PROCESSING,
  MSG_STILL_PENDING,
  useApproveStatus,
} from "../../shared/useApproveStatus";
import { MSG_LOST } from "../../shared/useFaceClaim";
import {
  APPROVE_ATTEMPTS,
  APPROVE_BUSY_WAIT_MS,
  MSG_APPROVE_RETRY_OFFLINE,
  approveFailureStatus,
  isApproveBusy,
  reportApproveTiming,
  sendApprove,
  useApproveRetry,
} from "../../shared/approveRetry";
import { prepareResend, RECOVERY } from "../../shared/approveRecovery";
import { getFaceClaimEvent } from "../../shared/faceClaimEvent";
import {
  MSG_APPROVE_ERROR,
  MSG_APPROVE_NOT_SENT,
  MSG_FACES_MISSING,
  showGuestNotice,
} from "../../shared/guestNotice";
import ApproveRetryNotice from "../../shared/ApproveRetryNotice";
import { returnHomeAfterAdminRelease } from "../../shared/adminRelease";
import { isFlowExpired } from "../../shared/useFlowInactivity";
import FlowCountdown from "../../shared/FlowCountdown";

// Hebrew UI texts of the claim-aware approve (the server's own `error` strings stay English).
const MSG_FACES_IN_USE = "חלק מהפרצופים כבר בשימוש, נא לבחור פרצופים אחרים";
const MSG_RESULT_PENDING = "התמונה הקודמת עדיין בעיבוד, נסו שוב בעוד רגע";

// Configuration object - can be moved to a separate config file
const CONFIG = {
  ENABLE_DOWNLOAD_FLOW: false, // When true, shows download prompt and enables download functionality
  ENABLE_BACKGROUND_TRIM: true, // Whether to enable automatic background trimming
  BACKGROUND_TRIM_THRESHOLD: 240, // Background detection threshold (0-255, 255 is pure white)
  TRIM_PADDING: 5, // Padding in pixels to leave around content in trimming
};

// Function to trim white or transparent background from image
const trimImageBackground = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      // If image is too small, don't perform trimming
      if (img.width < 50 || img.height < 50) {
        resolve(imageUrl);
        return;
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Draw the original image
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      // For performance testing
      console.time("trimImageBackground");

      // Background detection threshold
      const threshold = CONFIG.BACKGROUND_TRIM_THRESHOLD || 240;

      // Find content boundaries (not background)
      let minX = canvas.width;
      let minY = canvas.height;
      let maxX = 0;
      let maxY = 0;

      // Faster scanning - pixel sampling (not every pixel)
      // Scan every 3 pixels instead of every single pixel
      const sampleRate = 3;

      // Get pixel data and save in local scope (not as variable)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let y = 0; y < canvas.height; y += sampleRate) {
        for (let x = 0; x < canvas.width; x += sampleRate) {
          const index = (y * canvas.width + x) * 4;

          // Check if pixel is not white or transparent
          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          const a = data[index + 3];

          // If pixel is not transparent and not very close to white
          if (a > 20 && !(r > threshold && g > threshold && b > threshold)) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }
      console.timeEnd("trimImageBackground");

      // Add padding
      const padding = CONFIG.TRIM_PADDING || 5;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(canvas.width, maxX + padding);
      maxY = Math.min(canvas.height, maxY + padding);

      // Check if meaningful content was detected
      if (
        maxX <= minX ||
        maxY <= minY ||
        maxX - minX < 20 ||
        maxY - minY < 20 || // Prevent trimming images that are too small
        maxX - minX > canvas.width * 0.95 || // If content takes up most of image, don't trim
        maxY - minY > canvas.height * 0.95
      ) {
        console.error(
          "לא זוהה תוכן משמעותי לקיצוץ או התוכן ממלא רוב התמונה, מחזיר תמונה מקורית"
        );

        // Instead of delete imageData - mark for garbage collection that object is not in use
        // imageData = null; - not needed due to automatic variable scope

        resolve(imageUrl);
        return;
      }

      // Clean original canvas to free memory
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create new canvas with trimmed dimensions
      const trimmedCanvas = document.createElement("canvas");
      const trimmedCtx = trimmedCanvas.getContext("2d");

      trimmedCanvas.width = maxX - minX;
      trimmedCanvas.height = maxY - minY;

      // Draw only the desired part
      trimmedCtx.drawImage(
        img, // Use original image instead of canvas
        minX,
        minY,
        maxX - minX,
        maxY - minY,
        0,
        0,
        maxX - minX,
        maxY - minY
      );

      // Convert back to URL
      trimmedCanvas.toBlob((blob) => {
        // Cancel old URL to free memory
        if (imageUrl.startsWith("blob:")) {
          URL.revokeObjectURL(imageUrl);
        }

        const trimmedUrl = URL.createObjectURL(blob);
        resolve(trimmedUrl);
      }, "image/png");
    };
    img.onerror = () => {
      console.error("שגיאה בטעינת התמונה לקיצוץ");
      resolve(imageUrl); // Return original image in case of error
    };

    img.src = imageUrl;
  });
};

const SwingieResultPage = ({ siteRedirect }) => {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const { userId, eventName } = useAppContext();
  const serverUrl = SERVER_URL;
  const businessSiteUrl = process.env.REACT_APP_BUSINESS_SITE_URL;

  // Retrieve final image data from localStorage
  const [finalImageState, setFinalImageState] = useState(
    localStorage.getItem("finalImage")
  );
  const [processedImage, setProcessedImage] = useState(false);

  const [gifUrl, setGifUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Guards a double-tap on "אשר ושלח": none of the three entry points
  // (confirm modal / direct approve / close-modal-then-approve) carried any
  // disabled state, so a second tap fired a second, identical /approve
  // request — the first succeeded, the second 409'd and showed the guest a
  // scary generic failure despite the photo having landed. A ref because it
  // must be true synchronously on the SECOND tap, before any re-render could
  // reflect a disabled button (2026-08-04, live MAAYAN report — same gap
  // confirmed here on the Swingies side).
  const approveInFlightRef = useRef(false);
  // Browser trim duration — the client leg of the approve-timing report
  const trimSRef = useRef(null);
  const [isApproving, setIsApproving] = useState(false);
  // The server answers /approve at ENQUEUE time; "done" is learned by polling the claim status, and the
  // inactivity timer is paused for as long as the image is actually being processed.
  const approveStatus = useApproveStatus();
  // A thrown / aborted approve that the server never recorded is re-sent once silently, then offered as a button.
  const approveRetry = useApproveRetry();

  // Event visit this page belongs to. Late completions (trim, approve, the
  // redirect timers) must never write shared storage, hide the shared overlay
  // or navigate once another event (or a later visit of this one) replaced it,
  // so the scope is captured at mount, timers are tracked and cleared on
  // unmount, and every shared effect re-checks it.
  const scopeRef = useRef(getEventScope());
  const timersRef = useRef([]);
  const unmountedRef = useRef(false);
  const scheduleIfCurrent = (fn, ms) => {
    if (unmountedRef.current) return;
    const id = setTimeout(() => {
      if (isCurrentScope(scopeRef.current)) fn();
    }, ms);
    timersRef.current.push(id);
  };
  const isLive = () => !unmountedRef.current && isCurrentScope(scopeRef.current);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };
  }, []);

  // Safety guard: if no final image (e.g., browser back after redirect), go home
  useEffect(() => {
    if (!finalImageState) {
      navigate(`/${eventName}`, { replace: true });
      return;
    }

    // Handle bfcache restoration: when browser restores this page from cache
    // after redirect, reload to load the home URL (set by replaceState)
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Returns the corresponding local GIF URL for the given gifName
  const getGifUrl = (gifName) => {
    switch (gifName) {
      case "saving_result":
        return gifSavingResult;
      case "done":
        return gifDone;
      case "failed":
        return gifFailed;
      case "face_failed":
        return gifFaceFailed;
      default:
        return null;
    }
  };

  // Effect for trimming image background on initial load
  useEffect(() => {
    if (finalImageState && !processedImage) {
      // Only if enabled in configuration
      if (CONFIG.ENABLE_BACKGROUND_TRIM) {
        showLoading();
        const trimStartMs = performance.now();
        trimImageBackground(finalImageState)
          .then((trimmedImage) => {
            // A trim that finished after this page/event visit ended must not
            // overwrite the NEW visit's shared finalImage.
            if (!isLive()) {
              if (trimmedImage !== finalImageState) {
                URL.revokeObjectURL(trimmedImage);
              }
              return;
            }
            // Reported with the approve-timing row (browser leg)
            trimSRef.current =
              Math.round((performance.now() - trimStartMs) / 100) / 10;
            if (trimmedImage !== finalImageState) {
              // Update image in localStorage and state
              localStorage.setItem("finalImage", trimmedImage);
              setFinalImageState(trimmedImage);
            }
            setProcessedImage(true);
          })
          .catch((error) => {
            console.error("שגיאה בקיצוץ התמונה:", error);
            if (isLive()) setProcessedImage(true);
          })
          .finally(() => {
            if (isLive()) hideLoading();
          });
      } else {
        setProcessedImage(true);
        hideLoading();
      }
    } else {
      hideLoading();
    }
  }, [finalImageState, processedImage, showLoading, hideLoading]);

  // The approve ended without an outcome the guest can act on (the claim failed, or it was lost and cannot be sent
  // again in this visit, e.g. the event ended): the failed screen with a Hebrew message, then Home after 15 s.
  const endLost = (terminal) => {
    clearClaimState(eventName);
    setGifUrl(getGifUrl("failed"));
    const message = terminal === "failed" ? MSG_FAILED : MSG_APPROVE_NOT_SENT;
    showGuestNotice(message, 15000);
    scheduleIfCurrent(() => {
      navigate(`/${eventName}`, { state: { error: message } });
    }, 15000);
  };

  // Definite failure / lost selection: the failed screen with a Hebrew explanation, then the face gallery.
  const leaveToGallery = (message, gifName, ms) => {
    clearClaimState(eventName);
    approveStatus.abandon();
    setGifUrl(getGifUrl(gifName));
    showGuestNotice(message, ms);
    scheduleIfCurrent(() => {
      navigate(`/${eventName}/face-gallery`, { state: { error: message } });
    }, ms);
  };

  // A guest tap on "אשר ושלח". Everything that sends lives in attemptApprove, which the one silent re-send and
  // the "נסה שוב" button run again for the SAME request (same request id, same claim token).
  const handleApprove = async (download = false) => {
    if (approveInFlightRef.current) return; // a tap already in flight — ignore
    // Expired flow (latched synchronously by the capture-phase activity handler of THIS dispatch) or a
    // visit that is already over: no new work, no request, no shared write.
    if (isFlowExpired() || !isLive()) return;
    approveInFlightRef.current = true;
    setIsApproving(true);
    // Persisted BEFORE sending: a lost response / reload re-poll repeats THIS approve, never a new one.
    const { approveRid, approveAt } = ensureApproveRequest(eventName);
    const claimState = readClaimState(eventName) || { approveRid, approveAt };
    const request = { approveRid, claimToken: claimState.token || undefined, claimState, download };
    approveRetry.begin(request, approveAt);
    await attemptApprove(request);
  };

  // The "נסה שוב" button of the lost screen. One tap = one recovery + re-send; a double tap finds the offer already
  // taken. Offered for as long as the flow lives (its 90 s deadline ends the visit), whatever lease is left.
  const handleRetryApprove = async () => {
    if (isFlowExpired() || !isLive()) return;
    const request = approveRetry.takeOffer();
    if (!request) return;
    await attemptApprove(request, { recover: true, attempt: APPROVE_ATTEMPTS.BUTTON });
  };

  // Sends the approve once (bounded) and acts on the outcome. `recover` = this repeats a lost approve (the silent
  // re-send, or the "נסה שוב" button): approveRecovery.prepareResend() first renews the hold (the same request) or
  // acquires the same faces again (a NEW request), and only then is it sent. A repeat of the same request may meet an
  // earlier attempt still in server intake (409 busy). `timeoutMs` is the bound left by the editing lease (absent =
  // the full APPROVE_TIMEOUT_MS); `attempt` names the row in approve_timings.csv.
  const attemptApprove = async (
    initialRequest,
    { resend = false, timeoutMs, attempt = APPROVE_ATTEMPTS.FIRST, recover = false } = {}
  ) => {
    let request = initialRequest;
    let lease = null;
    // The approve belongs to the event visit it was tapped in.
    const scope = scopeRef.current;
    // Shared effects need BOTH: this page still mounted (same-event Back keeps
    // the generation but hands the shared state to the next page) and the
    // captured event visit still active.
    const stillCurrent = () => !unmountedRef.current && isCurrentScope(scope);

    // Processing time starts now: pause the flow inactivity timer until the outcome is known.
    approveStatus.hold();

    // Show waiting GIF
    setGifUrl(getGifUrl("saving_result"));
    // Set once the request is about to go out: a send that failed before that has nothing to report.
    let requestStartMs = null;
    let payloadKb = null;
    let timingReported = false;
    // The client half of approve_timings.csv for THIS attempt (answered, aborted or thrown): one row per attempt.
    const reportTiming = (status) => {
      timingReported = true;
      reportApproveTiming({
        serverUrl,
        eventName,
        attraction: "swingies",
        userId,
        requestStartMs,
        payloadKb,
        trimS: trimSRef.current,
        pixelRatio: sessionStorage.getItem("finalImagePixelRatio") || null,
        status,
        attempt,
        lease,
      });
    };
    // The approve got no usable answer (aborted, thrown, or a re-send that found the earlier attempt still in
    // intake). The response may have been lost AFTER the server accepted it: ask the claim by the persisted
    // request id before declaring a failure (410 = never accepted = the old behavior).
    // The ORIGINAL captured visit decides first: a late rejection from event A after A -> B (or A -> B -> A,
    // or leaving this result page) must not pause B's flow or send A's token/request id under B.
    const pollUnansweredApprove = () => {
      if (!stillCurrent()) return;
      approveStatus.start({
        requestId: request.approveRid,
        claimToken: request.claimToken,
        scope,
        onTerminal: (terminal) => {
          if (!stillCurrent()) return;
          if (terminal === "done") {
            clearClaimState(eventName);
            setGifUrl(getGifUrl("done"));
            scheduleIfCurrent(() => {
              localStorage.removeItem("finalImage");
              sessionStorage.removeItem("selectedFaces");
              sessionStorage.removeItem("selectedBodies");
              navigate(`/${eventName}`, { state: { imageUrl: null } });
            }, 6000);
            return;
          }
          if (terminal === "lost") {
            // 410 claim_unknown: the server never recorded this request, so it is safe to send it again. The first
            // loss re-sends silently (after renewing the hold or acquiring the faces again; the image is re-read
            // from localStorage.finalImage); a later loss shows the "נסה שוב" button, whatever lease is left
            // (KARNI 2026-10-25: losses at ~91 s used to get neither). The flow's own 90 s deadline ends the visit.
            if (approveRetry.takeSilentResend()) {
              attemptApprove(request, { recover: true, attempt: APPROVE_ATTEMPTS.AUTO });
              return;
            }
            clearClaimState(eventName);
            setGifUrl(getGifUrl("failed"));
            approveRetry.offer();
            return;
          }
          endLost(terminal);
        },
      });
    };

    if (recover) {
      // A lost approve is repeated only on a hold with enough lease left (see approveRecovery.js).
      const outcome = await prepareResend({
        event: getFaceClaimEvent(),
        request,
        leaseEndAt: approveRetry.leaseEnd(),
        isLive: stillCurrent,
      });
      if (!stillCurrent()) return;
      if (outcome.kind === RECOVERY.PROCESSING) {
        // The approve was recorded after all: its claim is processing, ask it instead of sending.
        pollUnansweredApprove();
        return;
      }
      if (outcome.kind === RECOVERY.REVOKED) {
        approveStatus.abandon();
        clearClaimState(eventName);
        returnHomeAfterAdminRelease(navigate);
        return;
      }
      if (outcome.kind === RECOVERY.TAKEN) {
        leaveToGallery(MSG_LOST, "failed", 6000);
        return;
      }
      if (outcome.kind === RECOVERY.ENDED) {
        approveStatus.abandon();
        endLost("lost");
        return;
      }
      if (outcome.kind === RECOVERY.OFFLINE) {
        // Neither the renewal nor the acquisition got through: keep the button (the next tap tries again).
        approveRetry.adopt(outcome.request || request, approveRetry.leaseEnd());
        approveStatus.abandon();
        clearClaimState(eventName);
        setGifUrl(getGifUrl("failed"));
        approveRetry.offer(MSG_APPROVE_RETRY_OFFLINE);
        return;
      }
      // Any repeat after a loss may meet an earlier attempt still in server intake (409 busy): wait and poll,
      // never read it as "faces in use" (also after a re-acquire, whose request id is new).
      resend = true;
      request = outcome.request;
      timeoutMs = outcome.timeoutMs;
      lease = outcome.lease;
      approveRetry.adopt(request, outcome.leaseEndAt);
      // The lost screen cleared the persisted claim record; the request is persisted again BEFORE sending, as at
      // the first tap: a reload while it is processed repeats it. A record that exists by now is never overwritten.
      if (!readClaimState(eventName)) writeClaimState(eventName, request.claimState);
    }
    const { approveRid, claimToken, download } = request;

    try {
      const selectedFaces =
        JSON.parse(sessionStorage.getItem("selectedFaces")) || [];
      const selectedBodies =
        JSON.parse(sessionStorage.getItem("selectedBodies")) || [];

      // Get current image from localStorage
      const currentImage = localStorage.getItem("finalImage");

      if (currentImage) {
        let imageToSend = currentImage;

        // Check if image is a blob URL and convert to base64 if needed
        if (currentImage.startsWith("blob:")) {
          try {
            // Fetch the blob data
            const response = await fetch(currentImage);
            const blob = await response.blob();

            // Convert blob to base64 data URL
            imageToSend = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            // Do not START the upload for an event visit that is already over.
            if (!stillCurrent()) return;
          } catch (error) {
            console.error("Error converting blob URL to base64:", error);
            throw new Error("Image processing error");
          }
        }

        payloadKb = Math.round(((imageToSend || "").length * 0.75) / 1024);
        requestStartMs = performance.now();
        // Bounded (APPROVE_TIMEOUT_MS, or the lease left for a re-send): an abort or a thrown fetch lands in the catch below.
        const { response, data: responseData } = await sendApprove({
          url: `${serverUrl}/approve?event_name=${eventName}`,
          timeoutMs,
          body: {
            image: imageToSend, // Use the converted image
            selected_faces: selectedFaces,
            selected_bodies: selectedBodies,
            UserId: userId,
            event_name: eventName,
            request_id: approveRid,
            ...(claimToken ? { claim_token: claimToken } : {}),
          },
        });

        // Fire-and-forget: the client half of approve_timings.csv. Never blocks
        // or fails the guest flow — the worker owns the server half.
        reportTiming(response.status);

        // The request above really happened for this event (and is reported);
        // everything below is local UI / SHARED state of the current visit.
        if (!stillCurrent()) return;

        // The approve is DONE for the guest only once the claim says so (see approveOutcome).
        const onApproved = async () => {
          clearClaimState(eventName);
          // Processing successful - show success animation
          setGifUrl(getGifUrl("done"));

          // Delete selected images if reFace is not active
          const reFace = sessionStorage.getItem("reFace");
          if (reFace === "false") {
            await deleteSelectedImages(
              eventName,
              BODY_STORE_NAME,
              selectedBodies
            );
            // A started write may finish, but no NEXT effect starts once this
            // visit is over (A -> B -> A included).
            if (!stillCurrent()) return;
            await deleteSelectedImages(
              eventName,
              FACE_STORE_NAME,
              selectedFaces
            );
            if (!stillCurrent()) return;
          }

          if (download) {
            await handleDownload();
          }

          // Navigate back after animation completes (only while this event
          // visit is still the active one)
          scheduleIfCurrent(() => {
            // Clean up image from localStorage and sessionStorage
            localStorage.removeItem("finalImage");
            sessionStorage.removeItem("selectedFaces");
            sessionStorage.removeItem("selectedBodies");

            // Revoke blob URL to prevent memory leaks
            if (finalImageState && finalImageState.startsWith("blob:")) {
              URL.revokeObjectURL(finalImageState);
            }

            // Redirect to business site if enabled and URL is configured
            // Rewrite history entry to home so browser Back goes to home (fresh start)
            if (siteRedirect !== false && businessSiteUrl) {
              window.history.replaceState(null, "", `/${eventName}`);
              window.location.href = businessSiteUrl;
            } else {
              navigate(`/${eventName}`, { state: { imageUrl: null } });
            }
          }, 6000);
        };

        // Polling outcome of an accepted (queued) approve.
        const approveOutcome = (terminal) => {
          if (!stillCurrent()) return;
          if (terminal === "done") onApproved();
          else if (terminal === "failed") leaveToGallery(MSG_FAILED, "failed", 6000);
          else leaveToGallery(MSG_LOST, "failed", 6000);
        };

        if (response.ok && responseData.success) {
          if (responseData.code && responseData.code !== "done") {
            // queued / running: poll the claim (timer paused meanwhile)
            approveStatus.start({
              requestId: approveRid,
              claimToken,
              scope,
              onTerminal: approveOutcome,
            });
          } else {
            // `done` (synchronous fallback) or a server that predates claims: finished now
            approveStatus.abandon();
            await onApproved();
          }
        } else if (resend && isApproveBusy(response, responseData)) {
          // A re-send met the earlier attempt of this same approve still in server intake (it holds the per-guest
          // lock and has recorded nothing yet): not "faces in use". Give the intake a moment to finish, then ask
          // the claim by the same request id; the processing hold stays on meanwhile.
          scheduleIfCurrent(pollUnansweredApprove, APPROVE_BUSY_WAIT_MS);
        } else {
          approveStatus.abandon();
          const code = responseData && responseData.code;
          // Handle error scenarios based on status code
          if (code === "claim_revoked") {
            // The operator released this guest's heads before the approve landed: start over at Home.
            clearClaimState(eventName);
            returnHomeAfterAdminRelease(navigate);
          } else if (code === "result_pending") {
            clearClaimState(eventName);
            setGifUrl(getGifUrl("failed"));
            showGuestNotice(MSG_RESULT_PENDING, 15000);
            scheduleIfCurrent(() => {
              navigate(`/${eventName}`, { state: { error: MSG_RESULT_PENDING } });
            }, 15000);
          } else if (
            response.status === 410 ||
            (response.status === 409 && code && code !== "faces_held") ||
            ["claim_expired", "faces_mismatch", "processing"].includes(code)
          ) {
            // A claim-layer refusal (expired, swapped, processing, bound to another guest id, request mismatch):
            // the hold is unusable for THIS guest, so the selection is lost. Never "faces in use", which blames
            // other guests (2026-10-18 incident); only the legacy answers (no code / used_faces) and faces_held
            // keep that screen.
            leaveToGallery(MSG_LOST, "failed", 6000);
          } else if (response.status === 409) {
            // Conflict - some faces are already in use
            clearClaimState(eventName);
            setGifUrl(getGifUrl("face_failed"));
            showGuestNotice(MSG_FACES_IN_USE, 10000);
            scheduleIfCurrent(() => {
              navigate(`/${eventName}/face-gallery`, {
                state: {
                  error: MSG_FACES_IN_USE,
                  usedFaces: responseData.used_faces || responseData.held_faces,
                },
              });
            }, 10000);
          } else if (response.status === 404) {
            // Not Found - missing faces
            setGifUrl(getGifUrl("failed"));
            showGuestNotice(MSG_FACES_MISSING, 15000);
            scheduleIfCurrent(() => {
              navigate(`/${eventName}`, {
                state: {
                  error: MSG_FACES_MISSING,
                  missingFaces: responseData.missing_faces,
                },
              });
            }, 15000);
          } else {
            // General error
            setGifUrl(getGifUrl("failed"));
            showGuestNotice(MSG_APPROVE_ERROR, 15000);
            scheduleIfCurrent(() => {
              navigate(`/${eventName}`, { state: { error: MSG_APPROVE_ERROR } });
            }, 15000);
          }
        }
      } else {
        setGifUrl(getGifUrl("failed"));
        showGuestNotice(MSG_APPROVE_ERROR, 15000);
        scheduleIfCurrent(() => {
          navigate(`/${eventName}`, { state: { error: MSG_APPROVE_ERROR } });
        }, 15000);
      }
    } catch (error) {
      // A request that went out and was aborted (APPROVE_TIMEOUT_MS) or threw is on record too; the event and
      // guest are this attempt's own, like the answered report above (an answered one is never reported twice).
      if (requestStartMs !== null && !timingReported) reportTiming(approveFailureStatus(error));
      pollUnansweredApprove();
    } finally {
      // The shared blob-URL registry and overlay belong to the CURRENT visit
      // only: a stale approve must not revoke B's URLs or hide B's loading.
      if (stillCurrent()) {
        try {
          // Release blob URLs from sessionStorage
          const faceUrls = JSON.parse(
            sessionStorage.getItem("faceImageBlobUrls") || "{}"
          );
          Object.values(faceUrls).forEach((url) => {
            if (url) URL.revokeObjectURL(url);
          });
          const bodyUrls = JSON.parse(
            sessionStorage.getItem("bodyImageBlobUrls") || "{}"
          );
          Object.values(bodyUrls).forEach((url) => {
            if (url) URL.revokeObjectURL(url);
          });
        } catch (error) {
          console.error("Error releasing blob URLs:", error);
        }
        hideLoading();
      }
    }
  };

  // Navigate back to the character editing page.
  const handleBack = () => {
    showLoading();
    navigate(`/${eventName}/edit-your-character`, {
      state: { imageUrl: null },
    });
  };

  // Handles the image download process.
  const handleDownload = async () => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const img = new Image();
    img.src = finalImageState;

    img.onload = () => {
      const desiredHeight = 1771; // Set your desired height in pixels
      const aspectRatio = img.width / img.height;
      const desiredWidth = desiredHeight * aspectRatio;

      canvas.width = desiredWidth;
      canvas.height = desiredHeight;

      // Fill canvas with white background
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);

      const offsetX = (canvas.width - desiredWidth) / 2;
      const offsetY = (canvas.height - desiredHeight) / 2;

      context.drawImage(img, offsetX, offsetY, desiredWidth, desiredHeight);

      canvas.toBlob(
        (blob) => {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "final-image.png";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        "image/png",
        1 // Maximum quality
      );
    };
  };

  // Confirm modal action to approve and download the image.
  const handleConfirm = () => {
    setIsModalOpen(false);
    handleApprove(true);
  };

  // Opens modal if download flow is enabled, otherwise directly approve.
  const handleOpenModal = () => {
    if (CONFIG.ENABLE_DOWNLOAD_FLOW) {
      setIsModalOpen(true);
    } else {
      handleApprove(false);
    }
  };

  // Close modal and proceed without download.
  const handleCloseModal = () => {
    setIsModalOpen(false);
    handleApprove(false);
  };

  // Cancel modal action.
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="swingie-result-container">
      <h1 className="swingie-result-title rtl">אשרו את בחירתכם</h1>
      <FlowCountdown variant="fixed" />
      {CONFIG.ENABLE_DOWNLOAD_FLOW && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
      {gifUrl && (
        <div className="swingie-result-gif-overlay">
          <img
            src={gifUrl}
            alt="Status GIF"
            className="swingie-result-status-gif"
          />
          {(approveStatus.phase === "processing" ||
            approveStatus.phase === "pending") && (
            <p
              className="rtl"
              data-testid="approve-status-text"
              style={{ textAlign: "center", fontWeight: 700 }}
            >
              {approveStatus.phase === "pending"
                ? MSG_STILL_PENDING
                : MSG_PROCESSING}
            </p>
          )}
        </div>
      )}{" "}
      {approveRetry.offered && (
        <ApproveRetryNotice onRetry={handleRetryApprove} message={approveRetry.offerMessage} />
      )}
      {/* Image container */}
      <div className="swingie-result-image-wrapper">
        {finalImageState ? (
          <img
            src={finalImageState}
            alt="Final Result"
            className="swingie-result-image"
          />
        ) : (
          <p>No image to display</p>
        )}
      </div>
      <div className="swingie-result-button-container">
        <button className="swingie-result-footer-button" onClick={handleBack}>
          חזרה
        </button>
        <button
          className="swingie-result-footer-button"
          onClick={handleOpenModal}
          disabled={isApproving}
        >
          אשר&nbsp;ושלח
        </button>
      </div>
    </div>
  );
};

export default SwingieResultPage;
